import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { auth, db } from '../firebase';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import ExternalLinks from '../components/ExternalLinks';
import UserProfile from '../components/UserProfile';
import UsersManagementHub from '../components/users/UsersManagementHub';
import TeamBroadcastsPanel from '../components/communications/TeamBroadcastsPanel';
import CompanyAppsStatusBoard from '../components/CompanyAppsStatusBoard';
import TicketsHub from '../components/tickets/TicketsHub';
import CompanyActivityLogPanel from '../components/CompanyActivityLogPanel';
import AlertsBellMenu from '../components/AlertsBellMenu';
import RoleWorkspace from '../components/dashboards/RoleWorkspace';
import { readShowAlertsOnOverview, writeShowAlertsOnOverview } from '../components/AlertNotificationSettingsDialog';
import { useCompanyAlerts } from '../hooks/useCompanyAlerts';
import Sidebar from '../components/Sidebar';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton,
  Alert,
} from '@mui/material';
import TenantSessionBadge from '../components/TenantSessionBadge';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KanbanBoard from '../components/agile/KanbanBoard';
import SprintManager from '../pages/SprintsHub';
import AppsAndSites from '../components/admin/AppsAndSites';
import WorkspaceSettings from '../components/admin/WorkspaceSettings';
import AiUsageSpendPanel from '../components/admin/AiUsageSpendPanel';
import AiSpendNavIndicator from '../components/AiSpendNavIndicator';
import ChatSettings from '../components/admin/ChatSettings';
import ChatOperationsHub from '../components/chat/ChatOperationsHub';
import ChatAlertsNotifier from '../components/chat/ChatAlertsNotifier';
import ReportIssueWidget from '../components/report-issue/ReportIssueWidget';
import AIOperationsCenter from '../components/AIOperationsCenter';
import CrmHub from './CrmHub';
import WorkspaceFooter from '../components/WorkspaceFooter';
import {
  dashboardTabPanelSx,
  DashboardMainContent,
  isFullViewportTab,
} from '../components/layout/DashboardLayout';
import GlobalAnnouncementModal from '../components/notifications/GlobalAnnouncementModal';
import { workspaceShellBackgroundSx } from '../lib/workspaceBranding';
import {
  DASHBOARD_TAB_PATHS,
  dashboardPathForTab,
  dashboardTabFromPathname,
  normalizeDashboardTab,
  parseTicketIdFromPathname,
  dashboardTicketPath,
} from '../lib/dashboardRoutes';
import {
  activeModuleKeyForTab,
  isDashboardPathAccessible,
  isDashboardTabAccessible,
} from '../lib/dashboardModuleAccess';
import ModuleNotActive from '../components/ModuleNotActive';
import { ACTIVE_MODULE_LABELS } from '../lib/companyActiveModules';
import { isModuleEnabled } from '../lib/companyProfile';
import { WorkspaceProvider, useWorkspace } from '../context/WorkspaceContext';
import LoadingLockScreen from '../components/LoadingLockScreen';
import ThemeModeToggle from '../components/ThemeModeToggle';
import DashboardToolbarLinks from '../components/DashboardToolbarLinks';
import NotificationsPage from '../components/settings/NotificationsPage';
import BillingManager from '../components/settings/BillingManager';
import ReportsHub from './ReportsHub';
import GameStudioHub from './GameStudioHub';
import ImageEditorHub from './ImageEditorHub';
import FilesHub from './FilesHub';
import AiStudioDashboard from '../components/ai-studio/AiStudioDashboard';
import TicketBriefingSpotCheck from './TicketBriefingSpotCheck';
import CodeSandboxReviewHub from '../components/code-sandbox/CodeSandboxReviewHub';
import {
  setTelemetryContext,
  trackModuleViewed,
  trackPermissionDenied,
} from '../lib/telemetry';
import { canAccessUsersSection, canAccessCodeSandboxReview, canAccessChatOperations, canAccessAiStudio, canAccessGameStudio, canAccessFiles, canAccessReports } from '../lib/userRoleHierarchy';
import { isAdminRole } from '../lib/rolePermissions';
import { enrichUserDataWithRichText } from '../lib/richText';

const drawerWidth = 240;

/** Dashboard tab → granular RBAC module for permission_denied telemetry. */
const TAB_PERMISSION_MODULE = {
  Portfolio: 'portfolio',
  Monitoring: 'monitoring',
  Activity: 'ticketing',
  Tickets: 'ticketing',
  ChatOperations: 'chatWidget',
  SprintPlanning: 'agile',
  Kanban: 'agile',
  Links: 'externalLinks',
  CodeSandboxReview: 'codeReview',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserData(enrichUserDataWithRichText(userDoc.data()));
          } else {
            setUserData(null);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          setUserData(null);
        }
      } else {
        navigate('/');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  return (
    <WorkspaceProvider companyId={userData?.companyId ?? null}>
      <DashboardShell userData={userData} authLoading={loading} onUserDataChange={setUserData} />
    </WorkspaceProvider>
  );
};

function DashboardShell({ userData, authLoading, onUserDataChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const outerTheme = useTheme();
  const { workspace, loading: workspaceLoading, refresh: refreshWorkspace } = useWorkspace();
  
  // UI States
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('Overview'); // Controls what is shown in the main area
  const [showAlertsOnOverview, setShowAlertsOnOverview] = useState(() => readShowAlertsOnOverview());
  const [ticketsOpenId, setTicketsOpenId] = useState(null);
  const [kanbanOpenTaskId, setKanbanOpenTaskId] = useState(null);
  const [alertDetailId, setAlertDetailId] = useState(null);
  const [ticketDraftFromAlert, setTicketDraftFromAlert] = useState(null);
  const [aiOpsOpen, setAiOpsOpen] = useState(false);

  const companyAlerts = useCompanyAlerts(userData);

  const handleShowAlertsOnOverviewChange = useCallback((value) => {
    setShowAlertsOnOverview(value);
    writeShowAlertsOnOverview(value);
  }, []);

  const navigateToTab = useCallback(
    (tabId, options = {}) => {
      const normalized = normalizeDashboardTab(tabId);
      setActiveTab(normalized);
      navigate(dashboardPathForTab(normalized), options);
    },
    [navigate],
  );

  const handleCreateTicketFromAlert = useCallback((item) => {
    const alertTitle = (item?.title || '').trim() || 'Notification';
    const prefilledTitle = `From notification: ${alertTitle}`.slice(0, 500);
    const msg = (item?.message || '').trim();
    const body = `${msg}\n\n—\nFrom notification · ID: ${item?.id || '—'}`.slice(0, 8000);
    setTicketDraftFromAlert({ title: prefilledTitle, body, fromAlertTitle: alertTitle });
    setAlertDetailId(null);
    navigateToTab('Tickets');
  }, [navigateToTab]);

  const consumeOpenTicket = useCallback(() => setTicketsOpenId(null), []);
  const consumeOpenTask = useCallback(() => setKanbanOpenTaskId(null), []);
  const consumeTicketDraft = useCallback(() => setTicketDraftFromAlert(null), []);

  const alertsRemote = useMemo(
    () => ({
      rows: companyAlerts.rows,
      loading: companyAlerts.loading,
      refresh: companyAlerts.refresh,
      unreadInInbox: companyAlerts.unreadInInbox,
    }),
    [companyAlerts.rows, companyAlerts.loading, companyAlerts.refresh, companyAlerts.unreadInInbox],
  );

  // Sync tab from URL path (/dashboard/portfolio, /dashboard/monitoring, …)
  useEffect(() => {
    const ticketIdFromPath = parseTicketIdFromPathname(location.pathname);
    if (ticketIdFromPath) {
      setTicketsOpenId(ticketIdFromPath);
      setActiveTab('Tickets');
      return;
    }
    const tabFromPath = dashboardTabFromPathname(location.pathname);
    if (tabFromPath) {
      setActiveTab(tabFromPath);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!userData) return;
    setTelemetryContext({
      role: userData?.role || '',
      companyId: userData?.companyId || null,
      module: activeTab,
    });
    trackModuleViewed(activeTab);
  }, [activeTab, userData?.role, userData?.companyId, userData]);

  useEffect(() => {
    if (authLoading || workspaceLoading || !userData) return;

    const role = userData?.role;

    const roleBlocked =
      ((activeTab === 'Workspace' ||
        activeTab === 'SettingsBilling' ||
        activeTab === 'CrmMarketing') &&
        role !== 'company_admin') ||
      (activeTab === 'AiSpend' && !isAdminRole(role)) ||
      (activeTab === 'TicketBriefings' && !isAdminRole(role)) ||
      (activeTab === 'AiStudio' && !canAccessAiStudio(role, workspace)) ||
      (activeTab === 'GameStudio' && !canAccessGameStudio(role, workspace)) ||
      (activeTab === 'ImageEditor' && !canAccessGameStudio(role, workspace)) ||
      (activeTab === 'Files' && !canAccessFiles(role, workspace)) ||
      (activeTab === 'Reports' && !canAccessReports(role, workspace)) ||
      (activeTab === 'CodeSandboxReview' &&
        !canAccessCodeSandboxReview(role, workspace));

    const moduleBlocked =
      activeTab !== 'Workspace' &&
      activeTab !== 'AiSpend' &&
      activeTab !== 'TicketBriefings' &&
      activeTab !== 'CrmMarketing' &&
      activeTab !== 'GameStudio' &&
      activeTab !== 'ImageEditor' &&
      activeTab !== 'Files' &&
      !isDashboardTabAccessible(workspace, activeTab, { loading: false });

    if (!roleBlocked && !moduleBlocked) return;

    if (roleBlocked) {
      trackPermissionDenied({
        tabId: activeTab,
        reason: 'role_blocked',
      });
      return;
    }

    trackPermissionDenied({
      module: TAB_PERMISSION_MODULE[activeTab] || null,
      action: 'read',
      tabId: activeTab,
      reason: 'module_not_active',
    });
  }, [activeTab, userData, workspace, workspaceLoading, authLoading]);

  // Route guard: blocked modular paths show ModuleNotActive (sidebar hides the link)
  useEffect(() => {
    if (authLoading || workspaceLoading || !userData) return;
    const normalizedPath = location.pathname.replace(/\/$/, '') || '/dashboard';
    if (
      userData?.role === 'super_admin' &&
      normalizedPath === DASHBOARD_TAB_PATHS.CodeSandboxReview
    ) {
      return;
    }
    if (!userData?.companyId) return;
    if (isDashboardPathAccessible(location.pathname, workspace, { loading: false })) return;

    const tabFromPath = dashboardTabFromPathname(location.pathname);
    if (!tabFromPath) return;

    setActiveTab(tabFromPath);
  }, [location.pathname, workspace, workspaceLoading, authLoading, userData]);

  // Deep links via location.state (tickets, notifications, legacy tab ids)
  useEffect(() => {
    const rawTab = location.state?.activeTab;
    const openTicketId = location.state?.openTicketId;
    const openTaskId = location.state?.openTaskId;
    const openAlertId = location.state?.openAlertId;
    if (!rawTab && !openTicketId && !openTaskId && !openAlertId) return;

    if (typeof openTicketId === 'string' && openTicketId) {
      setTicketsOpenId(openTicketId);
      setActiveTab('Tickets');
      navigate(dashboardTicketPath(openTicketId), { replace: true, state: {} });
      return;
    }
    if (typeof openTaskId === 'string' && openTaskId) {
      setKanbanOpenTaskId(openTaskId);
      setActiveTab('Kanban');
      navigate(DASHBOARD_TAB_PATHS.Kanban, { replace: true, state: {} });
      return;
    }
    if (typeof openAlertId === 'string' && openAlertId) {
      setActiveTab('Notifications');
      setAlertDetailId(openAlertId);
      navigate(DASHBOARD_TAB_PATHS.Notifications, { replace: true, state: {} });
      return;
    }
    if (typeof rawTab === 'string' && rawTab) {
      const tab = normalizeDashboardTab(rawTab);
      setActiveTab(tab);
      navigate(dashboardPathForTab(tab), { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const prevTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevTabRef.current === 'Notifications' && activeTab !== 'Notifications') {
      setAlertDetailId(null);
    }
    prevTabRef.current = activeTab;
  }, [activeTab]);

  const refreshUserData = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        onUserDataChange(enrichUserDataWithRichText(userDoc.data()));
      }
      await refreshWorkspace();
    } catch (e) {
      console.error('refreshUserData:', e);
    }
  }, [onUserDataChange, refreshWorkspace]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  // --- PLACEHOLDER COMPONENTS FOR OUR TABS ---
  const welcomeName =
    userData?.displayName ||
    userData?.fullName ||
    [userData?.firstName, userData?.lastName].filter(Boolean).join(' ') ||
    (userData?.email ? userData.email.split('@')[0] : null) ||
    'there';

  // Tab panels stay mounted (hidden when inactive) so Firestore listeners keep running.
  const tabPanels = useMemo(
    () => ({
      Overview: (
        <RoleWorkspace
          userData={userData}
          roleLoading={authLoading}
          welcomeName={welcomeName}
          onOpenTeam={() => navigateToTab('Users')}
          onOpenTickets={() => navigateToTab('Tickets')}
          onOpenMonitoring={() => navigateToTab('Monitoring')}
        />
      ),
      AiStudio: <AiStudioDashboard userData={userData} />,
      CodeSandboxReview: <CodeSandboxReviewHub userData={userData} />,
      Monitoring: <CompanyAppsStatusBoard userData={userData} />,
      Portfolio: <AppsAndSites userData={userData} />,
      Users: <UsersManagementHub userData={userData} />,
      Activity: <CompanyActivityLogPanel userData={userData} />,
      Notifications: (
        <NotificationsPage
          userData={userData}
          alertsRemote={alertsRemote}
          focusedAlertId={alertDetailId}
          onFocusedAlertChange={setAlertDetailId}
          onCreateTicketFromAlert={handleCreateTicketFromAlert}
          showAlertsOnOverview={showAlertsOnOverview}
          onChangeShowAlertsOnOverview={handleShowAlertsOnOverviewChange}
        />
      ),
      Tickets: (
        <TicketsHub
          userData={userData}
          openTicketId={ticketsOpenId}
          onConsumedOpenTicket={consumeOpenTicket}
          newTicketDraft={ticketDraftFromAlert}
          onConsumedNewTicketDraft={consumeTicketDraft}
        />
      ),
      ChatSettings: <ChatSettings />,
      ChatOperations: <ChatOperationsHub userData={userData} />,
      Links: <ExternalLinks userData={userData} />,
      SprintPlanning: <SprintManager userData={userData} />,
      Kanban: (
        <KanbanBoard
          userData={userData}
          openTaskId={kanbanOpenTaskId}
          onConsumedOpenTask={consumeOpenTask}
        />
      ),
      Workspace: <WorkspaceSettings userData={userData} />,
      AiSpend: <AiUsageSpendPanel userData={userData} />,
      TicketBriefings: <TicketBriefingSpotCheck userData={userData} />,
      CrmMarketing: <CrmHub userData={userData} />,
      CommsOutreach: <TeamBroadcastsPanel userData={userData} />,
      Profile: <UserProfile onSaved={refreshUserData} />,
      SettingsBilling: <BillingManager />,
      Reports: (
        <ReportsHub
          userData={userData}
          onGoBilling={
            userData?.role === 'company_admin'
              ? () => navigateToTab('SettingsBilling')
              : undefined
          }
        />
      ),
      GameStudio: <GameStudioHub userData={userData} />,
      ImageEditor: <ImageEditorHub userData={userData} />,
      Files: <FilesHub userData={userData} />,
    }),
    [
      userData,
      authLoading,
      welcomeName,
      alertsRemote,
      alertDetailId,
      handleCreateTicketFromAlert,
      ticketsOpenId,
      kanbanOpenTaskId,
      consumeOpenTask,
      consumeOpenTicket,
      ticketDraftFromAlert,
      consumeTicketDraft,
      refreshUserData,
      navigateToTab,
      showAlertsOnOverview,
      handleShowAlertsOnOverviewChange,
    ],
  );

  const sectionLabels = useMemo(
    () => ({
      Overview: 'Overview',
      AiStudio: 'AI Studio',
      CodeSandboxReview: 'Code sandbox review',
      Notifications: 'Settings · Notifications inbox',
      Portfolio: 'Product Portfolio',
      Monitoring: 'Uptime Monitoring',
      Users: 'Settings · Users & access',
      CommsOutreach: 'Communications · Team outreach',
      Activity: 'Activity',
      Tickets: 'Tickets',
      ChatOperations: 'Chat Operations',
      ChatSettings: 'Chat Settings',
      SprintPlanning: 'Sprint Planning',
      Kanban: 'Kanban Board',
      Links: 'External Links',
      Workspace: 'Settings · Workspace',
      AiSpend: 'Settings · AI spend',
      TicketBriefings: 'Settings · Ticket briefings',
      CrmMarketing: 'CRM & Marketing',
      Profile: 'My Profile',
      SettingsBilling: 'Settings · Billing & plan',
      Reports: 'Reports',
      GameStudio: 'AI Game Studio',
      ImageEditor: 'AI Image Editor',
      Files: 'Files',
    }),
    [],
  );

  const drawer = (
    <Sidebar
      userData={userData}
      workspace={workspace}
      activeTab={activeTab}
      navigateToTab={navigateToTab}
      setMobileOpen={setMobileOpen}
      drawerWidth={drawerWidth}
    />
  );

  const renderContent = () => (
    <>
      {Object.entries(tabPanels).map(([tabId, panel]) => {
        const moduleBlocked =
          tabId !== 'Workspace' &&
          tabId !== 'AiSpend' &&
          tabId !== 'TicketBriefings' &&
          tabId !== 'Reports' &&
          tabId !== 'CodeSandboxReview' &&
          tabId !== 'ChatSettings' &&
          tabId !== 'CrmMarketing' &&
          tabId !== 'GameStudio' &&
          tabId !== 'ImageEditor' &&
          tabId !== 'Files' &&
          Boolean(userData?.companyId) &&
          !workspaceLoading &&
          !isDashboardTabAccessible(workspace, tabId, { loading: false });
        const roleBlocked =
          (tabId === 'Workspace' || tabId === 'SettingsBilling' || tabId === 'CrmMarketing') &&
          userData?.role !== 'company_admin';
        const aiSpendBlocked =
          (tabId === 'AiSpend' || tabId === 'TicketBriefings') &&
          !isAdminRole(userData?.role);
        const usersBlocked =
          tabId === 'Users' && !canAccessUsersSection(userData?.role);
        const commsBlocked =
          tabId === 'CommsOutreach' && userData?.role !== 'company_admin';
        const codeReviewBlocked =
          tabId === 'CodeSandboxReview' &&
          !canAccessCodeSandboxReview(userData?.role, workspace);
        const chatSettingsBlocked =
          tabId === 'ChatSettings' &&
          userData?.role !== 'company_admin' &&
          userData?.role !== 'super_admin';
        const chatOpsBlocked =
          tabId === 'ChatOperations' &&
          !canAccessChatOperations(userData?.role);
        const aiStudioBlocked =
          tabId === 'AiStudio' && !canAccessAiStudio(userData?.role, workspace);
        const gameStudioBlocked =
          tabId === 'GameStudio' && !canAccessGameStudio(userData?.role, workspace);
        const imageEditorBlocked =
          tabId === 'ImageEditor' && !canAccessGameStudio(userData?.role, workspace);
        const filesBlocked =
          tabId === 'Files' && !canAccessFiles(userData?.role, workspace);
        const reportsBlocked =
          tabId === 'Reports' && !canAccessReports(userData?.role, workspace);
        const moduleKey = activeModuleKeyForTab(tabId);

        return (
          <Box
            key={tabId}
            role="tabpanel"
            hidden={activeTab !== tabId}
            sx={dashboardTabPanelSx(tabId, activeTab)}
          >
            {roleBlocked || usersBlocked || commsBlocked || codeReviewBlocked || chatSettingsBlocked || chatOpsBlocked || aiStudioBlocked || gameStudioBlocked || imageEditorBlocked || filesBlocked || reportsBlocked || aiSpendBlocked ? (
              <Alert severity="warning" sx={{ maxWidth: 560 }}>
                {tabId === 'AiStudio'
                  ? 'AI Studio is available to Company Admins, Developers, and Super Admins only.'
                  : tabId === 'AiSpend'
                    ? 'AI spend is available to Company Admins and Super Admins only.'
                  : tabId === 'TicketBriefings'
                    ? 'Ticket briefings spot-check is available to Company Admins and Super Admins only.'
                  : tabId === 'GameStudio'
                    ? 'AI Game Studio is available to Company Admins, Developers, and Super Admins only.'
                  : tabId === 'ImageEditor'
                    ? 'AI Image Editor is available to Company Admins, Developers, and Super Admins only.'
                  : tabId === 'Files'
                    ? 'Files is available to Company Admins, Developers, and Super Admins only.'
                  : tabId === 'Reports'
                    ? 'Reports are available to Company Admins and Managers only.'
                  : tabId === 'CodeSandboxReview'
                  ? 'Code review is disabled for your role or this workspace. Company admins can enable the AI code review module and grant access under Settings → Workspace → Role permissions.'
                  : tabId === 'ChatSettings'
                  ? 'Chat Settings are available to Company Admins and Super Admins only.'
                  : tabId === 'ChatOperations'
                    ? 'Chat Operations is available to Agents, Managers, and Company Admins when the Chat module is enabled.'
                  : tabId === 'SettingsBilling'
                  ? 'Billing settings are available to Company Admins only.'
                  : tabId === 'Users'
                    ? 'User management is available to Company Admins, Developers, and Managers.'
                    : tabId === 'CommsOutreach'
                      ? 'Team outreach is available to Company Admins only.'
                      : tabId === 'CrmMarketing'
                        ? 'CRM & Marketing is available to Company Admins only.'
                        : 'Workspace settings are available to Company Admins only.'}
              </Alert>
            ) : moduleBlocked ? (
              <ModuleNotActive
                moduleKey={moduleKey}
                moduleLabel={moduleKey ? ACTIVE_MODULE_LABELS[moduleKey] : sectionLabels[tabId]}
                onGoHome={() => navigateToTab('Overview')}
              />
            ) : (
              <>
                {panel}
                {!isFullViewportTab(tabId) && <WorkspaceFooter />}
              </>
            )}
          </Box>
        );
      })}
    </>
  );

  const hasCompanyLinkBar = Boolean(userData?.companyId);

  const dashboardBrandedTheme = useMemo(() => {
    if (!workspace?.portalBrandingEnabled) return null;
    const main = workspace.brandColor || outerTheme.palette.primary.main;
    try {
      return createTheme(outerTheme, { palette: { primary: { main } } });
    } catch {
      return null;
    }
  }, [outerTheme, workspace]);

  const shellSx = useMemo(
    () => ({
      display: 'flex',
      height: '100vh',
      width: '100vw',
      overflow: 'hidden',
      ...workspaceShellBackgroundSx(outerTheme, workspace),
    }),
    [outerTheme, workspace],
  );

  const pageLoading =
    authLoading ||
    !userData ||
    (Boolean(userData?.companyId) && workspaceLoading);

  const dashboardLayout = (
    <Box sx={shellSx}>
      {/* TOP APP BAR */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>

          <TenantSessionBadge userData={userData} workspace={workspace} />
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, ml: 1.5 }}>
            {sectionLabels[activeTab] || activeTab}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isAdminRole(userData?.role) && userData?.companyId ? (
              <AiSpendNavIndicator
                userData={userData}
                onOpen={() => {
                  navigateToTab('AiSpend');
                  setMobileOpen(false);
                }}
              />
            ) : null}
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Role:{' '}
              <Box component="span" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                {userData?.role?.replace(/_/g, ' ')}
              </Box>
            </Typography>
            <AlertsBellMenu
              userData={userData}
              alertsRemote={alertsRemote}
              onViewAllAlerts={() => {
                navigateToTab('Notifications');
                setMobileOpen(false);
              }}
              onOpenAlertDetail={(id) => {
                setAlertDetailId(id);
                navigateToTab('Notifications');
                setMobileOpen(false);
              }}
              showAlertsOnOverview={showAlertsOnOverview}
              onChangeShowAlertsOnOverview={handleShowAlertsOnOverviewChange}
            />
            {userData?.companyId &&
              isModuleEnabled(workspace, 'aiInsights') &&
              ['company_admin', 'developer', 'manager', 'super_admin'].includes(userData?.role) && (
              <IconButton
                color="primary"
                onClick={() => setAiOpsOpen(true)}
                title="AI Operations Center"
              >
                <AutoAwesomeIcon />
              </IconButton>
            )}
            <IconButton
              color={activeTab === 'Profile' ? 'primary' : 'inherit'}
              onClick={() => navigateToTab('Profile')}
              title="My profile"
            >
              <AccountCircleIcon />
            </IconButton>
            <ThemeModeToggle />
            <IconButton color="error" onClick={handleLogout} title="Logout">
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
        {hasCompanyLinkBar && (
          <Toolbar
            variant="dense"
            disableGutters
            sx={{
              minHeight: 44,
              px: 2,
              py: 0.25,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'grey.50'),
            }}
          >
            <DashboardToolbarLinks userData={userData} onOpenAllLinks={() => setActiveTab('Links')} />
          </Toolbar>
        )}
      </AppBar>

      {/* SIDEBAR DRAWER */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }} // Better open performance on mobile
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop Permanent Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* MAIN CONTENT AREA */}
      <DashboardMainContent drawerWidth={drawerWidth} hasCompanyLinkBar={hasCompanyLinkBar}>
        {renderContent()}
      </DashboardMainContent>
      <AIOperationsCenter
        open={aiOpsOpen}
        onClose={() => setAiOpsOpen(false)}
        userData={userData}
      />
      <GlobalAnnouncementModal userData={userData} />
      <ChatAlertsNotifier userData={userData} />
      <ReportIssueWidget userData={userData} user={auth.currentUser} />
    </Box>
  );

  return pageLoading ? (
    <LoadingLockScreen open />
  ) : dashboardBrandedTheme ? (
    <ThemeProvider theme={dashboardBrandedTheme}>{dashboardLayout}</ThemeProvider>
  ) : (
    dashboardLayout
  );
}

export default Dashboard;